import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import i18n from '../../i18n';
import { Fetch } from '../utils/Utils.js';
import { formatDate } from '../../utils/dateFormat.js';
import './Billing.css';
import SectionDescription from '../shared/SectionDescription';

// Billing model (2026-10): everyone is on the free plan until the owner buys a
// herd-size band through Stripe Checkout, monthly or annual. Bands, labels and
// amounts all come from GET /api/billing/plans (which reads Stripe by lookup
// key) — nothing about a price is hardcoded here. There is no trial.

// Stripe statuses that mean "there is a subscription, but it is not being
// paid". These users are sent to the billing portal, never to Checkout, and
// never see the free-plan view (the API reports plan "free" for them).
const PAYMENT_PROBLEM_STATUSES = ['past_due', 'unpaid', 'incomplete', 'paused'];

// Error codes POST /api/billing/checkout can return as {"error": code}.
// Fetch (Utils.js) translates the code by i18n key before calling the error
// callback, so the page only needs to tell a known, translated message from
// anything else (unknown codes, plain-text 500 bodies) to show a generic one.
const CHECKOUT_ERROR_CODES = [
  'invalid_request', 'invalid_band', 'invalid_interval', 'free_plan_no_checkout',
  'enterprise_contact_us', 'billing_owner_only', 'already_subscribed',
  'price_not_configured', 'stripe_unavailable',
];

const isPortuguese = () => (i18n.language || '').startsWith('pt');
const numberLocale = () => (isPortuguese() ? 'pt-BR' : 'en-US');

// Centavos → "R$ 99" (PT) / "R$99" (EN). Decimals only when the amount is
// not a whole number of reais.
const formatAmount = (minorUnits, currency) => {
  const whole = minorUnits % 100 === 0;
  return new Intl.NumberFormat(numberLocale(), {
    style: 'currency',
    currency: (currency || 'brl').toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);
};

const formatCount = (n) => Number(n).toLocaleString(numberLocale());

const bandLabel = (band) => {
  if (!band) return '';
  const label = band.label || {};
  return (isPortuguese() ? label.pt : label.en) || label.en || label.pt || band.key || '';
};

const IntervalToggle = ({ interval, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="billing-toggle-row" role="group">
      <button
        type="button"
        className={`billing-toggle-btn ${interval === 'month' ? 'active' : ''}`}
        aria-pressed={interval === 'month'}
        onClick={() => onChange('month')}
      >
        {t('billing_interval_month')}
      </button>
      <button
        type="button"
        className={`billing-toggle-btn ${interval === 'year' ? 'active' : ''}`}
        aria-pressed={interval === 'year'}
        onClick={() => onChange('year')}
      >
        {t('billing_interval_year')}
      </button>
    </div>
  );
};

// One paid band. mode "subscribe" shows the Subscribe button; "readonly"
// shows the price only (paid users, payment problems, non-owners).
const BandCard = ({ band, interval, currency, mode, highlight, badge, onSubscribe, loading, busy }) => {
  const { t } = useTranslation();
  const price = interval === 'year' ? band.annual : band.monthly;
  const hasAmount = price && price.amount !== null && price.amount !== undefined;
  const priceText = hasAmount
    ? t(interval === 'year' ? 'billing_price_year' : 'billing_price_month', { price: formatAmount(price.amount, currency) })
    : t('billing_price_unavailable');

  return (
    <div className={`billing-band-card ${highlight ? 'highlight' : ''}`}>
      {badge && <div className="billing-band-badge">{badge}</div>}
      <div className="billing-band-name">{bandLabel(band)}</div>
      <div className="billing-band-price">{priceText}</div>
      {mode === 'subscribe' && (
        <button
          type="button"
          className="billing-btn billing-btn-primary billing-band-btn"
          onClick={() => onSubscribe(band.key)}
          disabled={!hasAmount || busy}
        >
          {loading ? t('loading') + '...' : t('billing_subscribe')}
        </button>
      )}
    </div>
  );
};

const EnterpriseCard = ({ min }) => {
  const { t } = useTranslation();
  return (
    <div className="billing-band-card billing-enterprise-card">
      <div className="billing-band-name">{t('billing_enterprise_title')}</div>
      <div className="billing-band-desc">{t('billing_enterprise_desc', { min: formatCount(min) })}</div>
      <Link className="billing-btn billing-btn-secondary billing-band-btn" to="/contact">
        {t('billing_contact_us')}
      </Link>
    </div>
  );
};

const Billing = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState('month');
  const [checkoutBand, setCheckoutBand] = useState(null); // band key whose Subscribe button is working
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let pending = 2;
    const done = () => { pending -= 1; if (pending === 0) setLoading(false); };
    Fetch('api/billing/status', 'GET', null,
      (data) => { setStatus(data); done(); },
      () => done()
    );
    Fetch('api/billing/plans', 'GET', null,
      (data) => { setPlans(data); done(); },
      () => done()
    );
  }, []);

  const knownErrorOrGeneric = (message) =>
    CHECKOUT_ERROR_CODES.some((code) => t(code) === message) ? message : t('billing_error_generic');

  const handleSubscribe = (bandKey) => {
    setError(null);
    setCheckoutBand(bandKey);
    Fetch('api/billing/checkout', 'POST', JSON.stringify({ band: bandKey, interval: billingInterval }),
      (data) => {
        if (data?.url) { window.location.href = data.url; return; }
        setCheckoutBand(null);
        setError(t('billing_error_generic'));
      },
      (message) => {
        setCheckoutBand(null);
        setError(knownErrorOrGeneric(message));
      }
    );
  };

  const handleManageBilling = () => {
    setError(null);
    setPortalLoading(true);
    Fetch('api/billing/portal', 'POST', null,
      (data) => { if (data?.url) window.location.href = data.url; setPortalLoading(false); },
      () => setPortalLoading(false)
    );
  };

  const loadingLabel = t('loading') + '...';
  const currentUser = JSON.parse(localStorage.getItem('zapmanejo_user') || 'null');
  const isOwner = !currentUser?.role || currentUser.role === 'owner';

  if (loading) {
    return <div className="billing-page"><div>{loadingLabel}</div></div>;
  }

  const subscriptionStatus = status?.subscription_status;
  const hasPaymentProblem = PAYMENT_PROBLEM_STATUSES.includes(subscriptionStatus);
  const isPaid = status?.plan === 'paid';
  const isAdminPaid = isPaid && !status.band && !status.has_stripe_customer;

  const bands = plans?.bands || [];
  const currency = plans?.currency || 'brl';
  const currentBandKey = status?.band || plans?.current?.band || null;
  const currentBand = bands.find((b) => b.key === currentBandKey) || null;
  const currentInterval = status?.interval || plans?.current?.interval || 'month';
  const suggestedBandKey = plans?.suggested_band || null;

  // View, in priority order. A payment problem wins over everything so these
  // users never see the free-plan view (the API reports them as plan "free").
  let view = 'free';
  if (!status) view = 'unavailable';
  else if (hasPaymentProblem) view = 'problem';
  else if (isAdminPaid) view = 'admin_paid';
  else if (isPaid) view = 'paid';

  const manageButton = isOwner && status?.has_stripe_customer && (
    <button
      type="button"
      className="billing-btn billing-btn-secondary"
      onClick={handleManageBilling}
      disabled={portalLoading}
    >
      {portalLoading ? loadingLabel : t('manage_billing')}
    </button>
  );

  const ownerNotice = !isOwner && (
    <p className="billing-manager-notice">{t('billing_owner_only')}</p>
  );

  const plansUnavailable = !plans && (
    <div className="error-message">{t('billing_load_error')}</div>
  );

  const readonlyGrid = (interval) => plans && (
    <div className="billing-band-grid">
      {bands.map((band) => {
        const isCurrent = band.key === currentBandKey;
        return (
          <BandCard
            key={band.key}
            band={band}
            interval={interval}
            currency={currency}
            mode="readonly"
            highlight={isCurrent}
            badge={isCurrent ? t('billing_current_badge') : null}
          />
        );
      })}
    </div>
  );

  return (
    <div className="billing-page">
      <SectionDescription title={t("billing_section_title")} text={t("billing_section_text")} />
      <div className="billing-card">

        {view === 'unavailable' && (
          <div className="error-message">{t('billing_load_error')}</div>
        )}

        {view === 'problem' && (
          <>
            <div className="billing-warning">
              <div className="billing-warning-title">{t('billing_payment_problem_title')}</div>
              <p className="billing-warning-text">
                {t('billing_payment_problem', { band: bandLabel(currentBand) || currentBandKey || '' })}
              </p>
            </div>
            {ownerNotice}
            {manageButton && <div className="billing-actions">{manageButton}</div>}
            {plansUnavailable}
            {readonlyGrid(currentInterval)}
          </>
        )}

        {view === 'admin_paid' && (
          <div className="billing-plan-box">
            <h2>{t('billing_active_plan')}</h2>
            <p className="billing-plan-desc">{t('billing_active_plan_desc')}</p>
          </div>
        )}

        {view === 'paid' && (
          <>
            <div className="billing-plan-box">
              <h2>{t('billing_current_plan')}</h2>
              {(currentBand || currentBandKey) && (
                <div className="billing-plan-name">{bandLabel(currentBand) || currentBandKey}</div>
              )}
              <div className="billing-plan-meta">
                {currentInterval === 'year' ? t('billing_billed_year') : t('billing_billed_month')}
              </div>
              {status.next_billing_at && (
                <p className="billing-next">
                  {t('next_billing')}: <strong>{formatDate(status.next_billing_at)}</strong>
                </p>
              )}
              {ownerNotice}
              {manageButton && <div className="billing-actions">{manageButton}</div>}
            </div>
            {plansUnavailable}
            {plans && <p className="billing-change-note">{t('billing_change_plan_note')}</p>}
            {readonlyGrid(currentInterval)}
          </>
        )}

        {view === 'free' && (
          <>
            <div className="billing-plan-box">
              <h2>{t('billing_free_title')}</h2>
              <p className="billing-plan-desc">{t('billing_free_current')}</p>
              {plans && (
                <p className="billing-plan-desc">{t('billing_free_desc', { max: formatCount(plans.free?.max) })}</p>
              )}
              {ownerNotice}
              {manageButton && <div className="billing-actions">{manageButton}</div>}
            </div>

            {plansUnavailable}

            {plans && (
              <>
                <h3 className="billing-grid-title">{t('billing_choose_band')}</h3>
                <IntervalToggle interval={billingInterval} onChange={setBillingInterval} />
                {billingInterval === 'year' && (
                  <p className="billing-annual-note">{t('billing_annual_note')}</p>
                )}
                {error && <div className="error-message">{error}</div>}
                <div className="billing-band-grid">
                  {bands.map((band) => {
                    const isSuggested = band.key === suggestedBandKey;
                    return (
                      <BandCard
                        key={band.key}
                        band={band}
                        interval={billingInterval}
                        currency={currency}
                        mode={isOwner ? 'subscribe' : 'readonly'}
                        highlight={isSuggested}
                        badge={isSuggested ? t('billing_recommended') : null}
                        onSubscribe={handleSubscribe}
                        loading={checkoutBand === band.key}
                        busy={checkoutBand !== null}
                      />
                    );
                  })}
                  <EnterpriseCard min={plans.enterprise?.min} />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Billing;
